import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api-config';
// Don't import Firebase directly
// Remove Firebase imports as we're using the server API exclusively
import Note from './Note';
import Task from './Task';
import Arrow from './Arrow';
import ImageComponent from './ImageComponent';
import CodeComponent from './CodeComponent';

const ComponentManager = forwardRef(({ boardId }, ref) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [componentsChanged, setComponentsChanged] = useState(true); // Initialize to true to load components initially
  const [draggingComponentId, setDraggingComponentId] = useState(null); // Track which component is being dragged
  const { user } = useAuth();
  
  // Refs to store timestamps
  const lastUpdateTimestampsRef = React.useRef({});
  const lastDragTimestampsRef = React.useRef({}); // Track when components were last dragged

  // Fetch components from the server and set up polling for updates
  useEffect(() => {
    if (!user || !boardId) {
      return;
    }
    
    setLoading(true);
    
    let componentPolling = null;
    let boardPolling = null;
    let isPollingActive = true;
    
    // Get component data updates from the server
    const getComponentUpdates = async (componentId) => {
      try {
        if (!isPollingActive) return;
        
        // Skip temporary components
        if (componentId.toString().startsWith('temp-')) {
          return;
        }
        
        // Check if component was recently dragged
        const lastDragTime = lastDragTimestampsRef.current[componentId] || 0;
        const timeSinceDrag = Date.now() - lastDragTime;
        const wasRecentlyDragged = timeSinceDrag < 5000; // 5 seconds in milliseconds
        
        // Skip polling completely for recently dragged components to avoid position conflicts
        if (wasRecentlyDragged) {
          console.log(`Component ${componentId} was dragged ${timeSinceDrag}ms ago - skipping polling to avoid position conflicts`);
          return; // Skip polling completely for recently dragged components
        }
        
        // Check if user has getIdToken method
        if (typeof user.getIdToken !== 'function') {
          console.error('Error fetching component data: user.getIdToken is not a function');
          setError('Authentication error. Please try logging in again.');
          return;
        }
        
        const token = await user.getIdToken();
        try {
          const response = await api.get(`/api/boards/${boardId}/components/${componentId}/data`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            validateStatus: function (status) {
              // Accept 200 (OK with data) and 204 (No Content) as valid responses
              return status === 200 || status === 204;
            }
          });
          
          // Only process data if we got a 200 response with actual data
          // 204 No Content means nothing has changed, so we don't need to update state
          if (response.status === 200 && response.data && Object.keys(response.data).length > 0) {
            
            // Update the state with the new data
            setComponents(prev => {
              return prev.map(comp => {
                if (comp.id === componentId) {
                  // If this component is currently being dragged or was recently dragged,
                  // only update non-position data to avoid position conflicts
                  if (comp.id === draggingComponentId || wasRecentlyDragged) {
                    const { position, points, ...otherData } = response.data;
                    // Preserve both position and points (for arrows) for dragged components
                    return { ...comp, ...otherData };
                  } else {
                    // For components not being dragged, update all data
                    return { ...comp, ...response.data };
                  }
                }
                return comp;
              });
            });
          } else if (response.status === 204) {
            // 204 No Content - no changes to process
            console.log(`No changes for component ${componentId}`);
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            // Remove this component from the local state if it's not found on the server
            setComponents(prev => prev.filter(comp => comp.id !== componentId));
            // Remove from lastUpdateTimestamps
            delete lastUpdateTimestampsRef.current[componentId];
          } else {
            console.error(`Error polling component ${componentId} data:`, error);
          }
        }
      } catch (err) {
        console.error(`Error in getComponentUpdates for ${componentId}:`, err);
      }
    };
    
    // Poll for board-level updates (new components, deleted components)
    const pollBoardUpdates = async () => {
      if (!isPollingActive) return;
      
      try {
        // Check if user has getIdToken method
        if (typeof user.getIdToken !== 'function') {
          console.error('Error polling board updates: user.getIdToken is not a function');
          return;
        }
        
        const token = await user.getIdToken();
        const response = await api.get(`/api/boards/${boardId}/components`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        
        if (response.data) {
          const serverComponents = response.data;
          
          // Get current component IDs from state
          setComponents(currentComponents => {
            const currentIds = currentComponents
              .filter(comp => !comp.id.toString().startsWith('temp-'))
              .map(comp => comp.id);
            const serverIds = serverComponents.map(comp => comp.id);
            
            // Find new components (on server but not in current state)
            const newComponents = serverComponents.filter(
              serverComp => !currentIds.includes(serverComp.id)
            );
            
            // Find deleted components (in current state but not on server)
            const deletedIds = currentIds.filter(
              id => !serverIds.includes(id)
            );
            
            // Process all server components - preserve position for recently dragged components
            const processedServerComponents = serverComponents.map(serverComp => {
              // Check if this component was recently dragged
              const lastDragTime = lastDragTimestampsRef.current[serverComp.id] || 0;
              const timeSinceDrag = Date.now() - lastDragTime;
              const wasRecentlyDragged = timeSinceDrag < 5000; // 5 seconds
              
              if (wasRecentlyDragged) {
                // Find the current component to preserve its position
                const currentComp = currentComponents.find(comp => comp.id === serverComp.id);
                if (currentComp && currentComp.position) {
                  console.log(`Preserving position for recently dragged component ${serverComp.id}`);
                  return { ...serverComp, position: currentComp.position };
                }
              }
              return serverComp;
            });
            
            // Extract new components from processed server components
            const processedNewComponents = processedServerComponents.filter(
              serverComp => !currentIds.includes(serverComp.id)
            );
            
            // Initialize lastUpdateTimestamps for new components
            processedNewComponents.forEach(component => {
              lastUpdateTimestampsRef.current[component.id] = Date.now();
            });
            
            // Create a new array of components by merging current components with processed server components
            // This ensures that we preserve the position for recently dragged components
            const updatedComponents = currentComponents.map(currentComp => {
              // Skip temporary components
              if (currentComp.id.toString().startsWith('temp-')) {
                return currentComp;
              }
              
              // Find the corresponding server component
              const serverComp = processedServerComponents.find(sc => sc.id === currentComp.id);
              
              // If this component exists on the server, use the processed server component
              // Otherwise, keep the current component (it might be deleted in the next update)
              return serverComp || currentComp;
            });
            
            // Add new components
            const componentsWithNew = [
              ...updatedComponents.filter(comp => !deletedIds.includes(comp.id)),
              ...processedNewComponents
            ];
            
            // Remove deleted components from lastUpdateTimestamps
            if (deletedIds.length > 0) {
              deletedIds.forEach(id => {
                delete lastUpdateTimestampsRef.current[id];
              });
            }
            
            return componentsWithNew;
          });
        }
      } catch (err) {
        console.error('Error polling board updates:', err);
      }
    };
    
    // Fetch all components and start polling
    const fetchComponents = async () => {
      try {
        // Check if user has getIdToken method
        if (typeof user.getIdToken !== 'function') {
          console.error('Error fetching components: user.getIdToken is not a function');
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        
        const token = await user.getIdToken();
        const response = await api.get(`/api/boards/${boardId}/components`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        
        if (response.data) {
          // Process components to preserve positions for recently dragged components
          const serverComponents = response.data;
          
          // Get current components to check for recently dragged ones
          setComponents(currentComponents => {
            // Process all server components - preserve position for recently dragged components
            const processedComponents = serverComponents.map(serverComp => {
              // Check if this component was recently dragged
              const lastDragTime = lastDragTimestampsRef.current[serverComp.id] || 0;
              const timeSinceDrag = Date.now() - lastDragTime;
              const wasRecentlyDragged = timeSinceDrag < 5000; // 5 seconds
              
              if (wasRecentlyDragged) {
                // Find the current component to preserve its position
                const currentComp = currentComponents.find(comp => comp.id === serverComp.id);
                if (currentComp && currentComp.position) {
                  console.log(`Initial fetch: Preserving position for recently dragged component ${serverComp.id}`);
                  return { ...serverComp, position: currentComp.position };
                }
              }
              return serverComp;
            });
            
            // Create a new array of components by merging current components with processed server components
            // This ensures that we preserve the position for recently dragged components
            const updatedComponents = currentComponents.map(currentComp => {
              // Skip temporary components
              if (currentComp.id.toString().startsWith('temp-')) {
                return currentComp;
              }
              
              // Find the corresponding server component
              const serverComp = processedComponents.find(sc => sc.id === currentComp.id);
              
              // If this component exists on the server, use the processed server component
              // Otherwise, keep the current component (it might be deleted in the next update)
              return serverComp || currentComp;
            });
            
            // Add any new components from the server that aren't in the current components
            const currentIds = currentComponents
              .filter(comp => !comp.id.toString().startsWith('temp-'))
              .map(comp => comp.id);
            
            const newComponents = processedComponents.filter(
              serverComp => !currentIds.includes(serverComp.id)
            );
            
            const finalComponents = [...updatedComponents, ...newComponents];
            
            // Initialize lastUpdateTimestamps for each component
            finalComponents.forEach(component => {
              if (!component.id.toString().startsWith('temp-')) {
                lastUpdateTimestampsRef.current[component.id] = Date.now();
              }
            });
            
            return finalComponents;
          });
          
          // Set componentsChanged to true initially to fetch data for all components
          setComponentsChanged(true);
          
          // Set up polling for component data updates
          componentPolling = setInterval(() => {
            if (isPollingActive) {
              // Get the current components from state to include any newly added ones
              setComponents(currentComponents => {
                // Poll for updates for all permanent components
                currentComponents.forEach(component => {
                  if (!component.id.toString().startsWith('temp-')) {
                    getComponentUpdates(component.id);
                  }
                });
                return currentComponents;
              });
            }
          }, 5000); // Poll every 5 seconds to reduce backend load
          
          // Set up polling for board-level updates (new/deleted components)
          boardPolling = setInterval(() => {
            if (isPollingActive) {
              pollBoardUpdates();
            }
          }, 10000); // Poll every 10 seconds to reduce backend load
        }
        
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching components:', err);
        setError('Failed to load components: ' + err.message);
        setLoading(false);
      }
    };
    
    // Start fetching components
    fetchComponents();
    
    return () => {
      isPollingActive = false;
      if (componentPolling) {
        clearInterval(componentPolling);
      }
      if (boardPolling) {
        clearInterval(boardPolling);
      }
    };
  }, [boardId, user]);

  // Expose methods to parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    addComponent: (type) => addComponent(type)
  }));

  // Add a new component through the server API only
  const addComponent = async (type) => {
    if (!user || !boardId) {
      return;
    }
    
    try {      
      // Get user ID safely
      const userId = user.uid || (user.user && user.user.uid) || 'unknown-user';      
      // Create a temporary ID for immediate feedback
      const tempId = `temp-${Date.now()}`;
      
      // Default position - place new components in a visible area
      const defaultPosition = { x: 100, y: 100 };
      
      // Create a new component with default values
      const newComponent = {
        type,
        position: defaultPosition,
        ...(type === 'note' ? { content: '' } : {}),
        ...(type === 'task' ? { tasks: [{ id: Date.now(), text: '', completed: false }] } : {}),
        ...(type === 'arrow' ? { 
          points: { 
            start: { x: 100, y: 100 }, 
            control: { x: 200, y: 50 }, 
            end: { x: 300, y: 100 } 
          } 
        } : {}),
        ...(type === 'code' ? { 
          content: '// Write your code here\n', 
          language: 'javascript' 
        } : {}),
        createdBy: userId,
        boardId: boardId // Explicitly add boardId to the component data
      };
            
      // Add to local state immediately for instant feedback
      const tempComponent = {
        id: tempId,
        ...newComponent
      };
      
      setComponents(prev => [...prev, tempComponent]);
      
      // Make sure we have a valid token before proceeding
      if (typeof user.getIdToken !== 'function') {
        throw new Error('user.getIdToken is not a function');
      }
      
      const token = await user.getIdToken();
      
      // Send component data to the server API
      const response = await api.post(
        `/api/boards/${boardId}/components`,
        newComponent,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const addedComponent = response.data;
      
      // Replace temp component with the real one in local state
      setComponents(prev => 
        prev.map(comp => 
          comp.id === tempId ? { ...comp, id: addedComponent.id } : comp
        )
      );
      
      // Signal that components have changed
      setComponentsChanged(true);
    } catch (err) {
      console.error('Error adding component:', err);
      setError('Failed to add component: ' + err.message);
    }
  };

  // Update a component through the server API only
  const updateComponent = async (id, data) => {
    if (!user || !boardId) return;
    
    try {
      // Don't update for temporary components
      if (id.toString().startsWith('temp-')) return;
      
      // Check if user has getIdToken method
      if (typeof user.getIdToken !== 'function') {
        console.error('Error updating component via API: user.getIdToken is not a function');
        setError('Authentication error. Please try logging in again.');
        return;
      }
      
      const token = await user.getIdToken();
      
      // Handle position updates with a specific API endpoint
      if (data.position) {
        // Skip position updates for components that are currently being dragged
        // This prevents position conflicts between local dragging and server updates
        if (id === draggingComponentId) {          
          // If there are other data to update besides position, continue with those
          const { position, ...otherData } = data;
          
          if (Object.keys(otherData).length > 0) {            
            await api.put(
              `/api/boards/${boardId}/components/${id}`,
              otherData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            // Update local state for immediate UI feedback (non-position data only)
            setComponents(prev => 
              prev.map(comp => 
                comp.id === id ? { ...comp, ...otherData } : comp
              )
            );
            
            // Signal that components have changed
            setComponentsChanged(true);
            
            // Update the last update timestamp for this component
            lastUpdateTimestampsRef.current[id] = Date.now();
          }
          
          return; // Skip the rest of the function for position updates while dragging
        }
        
        console.log('Updating position via API:', id, data.position);
        
        // Update position through server API
        await api.put(
          `/api/boards/${boardId}/components/${id}/position`,
          { position: data.position },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Remove position from data to avoid duplicate updates
        const { position, ...otherData } = data;
        
        // If there are other data to update besides position, send them separately
        if (Object.keys(otherData).length > 0) {
          
          await api.put(
            `/api/boards/${boardId}/components/${id}`,
            otherData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        }
      } else {
        // For non-position updates, use the main component update API        
        await api.put(
          `/api/boards/${boardId}/components/${id}`,
          data,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      // Update local state for immediate UI feedback
      setComponents(prev => 
        prev.map(comp => 
          comp.id === id ? { ...comp, ...data } : comp
        )
      );
      
      // Signal that components have changed
      setComponentsChanged(true);
      
      // Update the last update timestamp for this component
      lastUpdateTimestampsRef.current[id] = Date.now();
    } catch (err) {
      console.error('Error updating component:', err);
      setError('Failed to update component: ' + err.message);
    }
  };

  // Delete a component through the server API only
  const deleteComponent = async (id) => {
    if (!user || !boardId) return;
    
    try {
      // Don't delete for temporary components
      if (id.toString().startsWith('temp-')) return;
      
      // Skip deleting components that are currently being dragged
      if (id === draggingComponentId) {
        return;
      }
      
      // Delete the component through the server API
      
      // Check if user has getIdToken method
      if (typeof user.getIdToken !== 'function') {
        console.error('Error deleting component via API: user.getIdToken is not a function');
        setError('Authentication error. Please try logging in again.');
        return;
      }
      
      const token = await user.getIdToken();
      
      // First delete on the server
      await api.delete(`/api/boards/${boardId}/components/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      
      // Then update local state
      setComponents(prev => prev.filter(comp => comp.id !== id));
      
      // Signal that components have changed
      setComponentsChanged(true);
      
      // Remove this component from the lastUpdateTimestamps
      delete lastUpdateTimestampsRef.current[id];
    } catch (err) {
      console.error('Error deleting component:', err);
      setError('Failed to delete component: ' + err.message);
    }
  };

  // Handle component drag start
  const handleDragStart = (componentId) => {
    setDraggingComponentId(componentId);
    
    // Record the time when this component's drag started
    // This ensures polling is skipped during dragging as well
    lastDragTimestampsRef.current[componentId] = Date.now();
    console.log(`Recorded drag start timestamp for component ${componentId}: ${lastDragTimestampsRef.current[componentId]}`);
  };
  
  // Handle component drag move - update timestamp on every frame during dragging
  const handleDragMove = (componentId) => {
    if (componentId) {
      // Update the timestamp on every frame during dragging
      lastDragTimestampsRef.current[componentId] = Date.now();
    }
  };
  
  // Handle component drag end
  const handleDragEnd = (updatedPosition) => {
    
    // Record the time when this component's drag ended
    if (draggingComponentId) {
      lastDragTimestampsRef.current[draggingComponentId] = Date.now();
      console.log(`Recorded drag timestamp for component ${draggingComponentId}: ${lastDragTimestampsRef.current[draggingComponentId]}`);
      
      // If we received an updated position from the component, update our local state immediately
      if (updatedPosition) {
        setComponents(prev => 
          prev.map(comp => 
            comp.id === draggingComponentId ? { ...comp, position: updatedPosition } : comp
          )
        );
      }
    }
    
    setDraggingComponentId(null);
    
    // Set componentsChanged to true to trigger an update after dragging ends
    setComponentsChanged(true);
  };

  // Render a component based on its type
  const renderComponent = (component) => {
    switch (component.type) {
      case 'note':
        return (
          <Note
            key={component.id}
            id={component.id}
            initialContent={component.content}
            initialPosition={component.position}
            initialSize={component.size || { width: 200, height: 200 }}
            onUpdate={updateComponent}
            onDelete={deleteComponent}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        );
      case 'task':
        return (
          <Task
            key={component.id}
            id={component.id}
            initialTasks={component.tasks}
            initialPosition={component.position}
            initialSize={component.size || { width: 200, height: 200 }}
            onUpdate={updateComponent}
            onDelete={deleteComponent}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        );
      case 'arrow':
        return (
          <Arrow
            key={component.id}
            id={component.id}
            initialPoints={component.points}
            initialPosition={component.position}
            onUpdate={updateComponent}
            onDelete={deleteComponent}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        );
      case 'image':
        return (
          <ImageComponent
            key={component.id}
            id={component.id}
            initialImageUrl={component.content}
            initialPosition={component.position}
            initialSize={component.size || { width: 300, height: 300 }}
            onUpdate={updateComponent}
            onDelete={deleteComponent}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        );
      case 'code':
        return (
          <CodeComponent
            key={component.id}
            id={component.id}
            initialContent={component.content}
            initialLanguage={component.language || 'javascript'}
            initialPosition={component.position}
            initialSize={component.size || { width: 400, height: 300 }}
            onUpdate={updateComponent}
            onDelete={deleteComponent}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="component-manager-loading">Loading components...</div>;
  }

  if (error) {
    return <div className="component-manager-error">{error}</div>;
  }

  return (
    <div className="component-manager">
      <div className="component-toolbar">
        <button onClick={() => addComponent('note')}>Add Note</button>
        <button onClick={() => addComponent('task')}>Add Task</button>
        <button onClick={() => addComponent('arrow')}>Add Arrow</button>
        <button onClick={() => addComponent('image')}>Add Image</button>
        <button onClick={() => addComponent('code')}>Add Code</button>
      </div>
      <div className="component-container">
        {components.map(component => renderComponent(component))}
      </div>
    </div>
  );
});

export default ComponentManager;
